import React, { type ComponentType, type CSSProperties, useMemo, useRef } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import { injectIntl } from '@atlassian/jira-intl';
import {
	TitleWithIcon,
	Sections,
	Spinner,
	type SectionRendererArgs,
	type ConfigurableDate,
	VIEW_MODES,
} from '@atlassian/jira-portfolio-3-common';
import { ISSUE_FIELDS } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues/types';
import { defaultSorting } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/visualisations';
import { ADD_FIELDS_COLUMN_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/field-columns/types';
import { getConfiguredDateTooltip } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/messages';
import type { IssueColumn } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/types';
import { withSlots, slots } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import {
	DEFAULT_FIELD_WIDTH,
	SECTION_KEYS,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { Row, Left, Right } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/layout/row';
import type { MenuTriggerProps } from './advanced-fields-menu/types';
import AdvancedFieldsTrigger from './advanced-fields-trigger';
import CollapseButton from './collapse-button';
import messages from './messages';
import ResetSortingOrder from './reset-sorting-order';
import SortButton from './sort-button';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

type SectionRef = HTMLDivElement & {
	collapseSection: (i: number) => void;
};

const Header = (props: Props) => {
	const {
		intl,
		changeSort,
		getMatchingConfiguredDate,
		columns,
		customFields,
		sorting,
		planningUnit,
		issuePrioritiesIdList,
		AdvancedFieldsMenu,
		hideColumn,
		showColumn,
		moveColumn,
		width,
		onCollapseAllFields,
		onDragStart,
		onDragEnd,
		areCustomFieldsReady,
		viewNamespace,
		isExportMode,
		viewMode,
		isConfluenceMacro,
		minWidth,
	} = props;

	const sectionsRef = useRef<SectionRef>();
	const headerContainer = useRef<HTMLDivElement>(null);

	const visibleColumns = useMemo(() => columns.filter((column) => column.isVisible), [columns]);

	const collapse = (index: number) => () => {
		if (sectionsRef.current) {
			sectionsRef.current.collapseSection(index);
		}
	};

	const onKeyDownToggle = (index: number) => (event: React.KeyboardEvent) => {
		// Space bar is ' '
		if (event.key === 'Enter' || event.key === ' ') {
			collapse(index);
		}
	};

	const getColumnTitle = (column: IssueColumn) => {
		if (typeof column.title === 'string') {
			return column.title;
		}

		return intl.formatMessage(column.title);
	};

	const renderEmptyFields = () => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={`${styles['column-header']} ${styles['no-fields-header']}`}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<span className={styles['no-fields-text']}>{intl.formatMessage(messages.noFields)}</span>
		</div>
	);

	const renderMenuTrigger = (menuTriggerProps: MenuTriggerProps) => (
		<AdvancedFieldsTrigger numberOfVisibleFields={visibleColumns.length} {...menuTriggerProps} />
	);

	const renderAddFieldsColumnHeader = () =>
		!isExportMode && !isConfluenceMacro ? (
			<>
				<AdvancedFieldsMenu
					id="add-fields-header"
					columns={columns}
					hideColumn={hideColumn}
					showColumn={showColumn}
					moveColumn={moveColumn}
					getMatchingConfiguredDate={getMatchingConfiguredDate}
					isShortcutEnabled={false}
					renderMenuTrigger={renderMenuTrigger}
				/>
				{visibleColumns.length === 1 && renderEmptyFields()}
			</>
		) : undefined;

	const sectionRenderer = ({ section, onSectionClick, index, setHovered }: SectionRendererArgs) => {
		const {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			width,
			id,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			minWidth,
			title,
			isLoading,
			collapsible = true,
			HeaderContent = null,
		} = section;
		const configurableDate = getMatchingConfiguredDate(id);
		const tooltip = getConfiguredDateTooltip(configurableDate);
		const currentColumn = columns.reduce((result, column) => {
			if (column.id === id) {
				return column;
			}
			return result;
		}, columns[0] /** This default value will never be used as a column with matching id will always be found */);

		const titleTooltip = tooltip ? intl.formatMessage(tooltip, { title }) : title;

		return (
			<Sections.Item
				id={id}
				key={`section-${id}`}
				index={index}
				width={width}
				minWidth={minWidth}
				onClick={onSectionClick}
				setHovered={setHovered}
				collapsible={collapsible}
			>
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={`${styles['column-header']} ${
						styles[collapsible ? 'no-focus-collapsible' : 'no-focus']
					}`}
					key={id}
					onClick={collapsible ? collapse(index) : undefined}
					onKeyDown={collapsible ? onKeyDownToggle(index) : undefined}
					role="button"
					tabIndex={0}
					data-name={`field-header-${id}`}
					data-test-class="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fields.header"
				>
					{isLoading && (
						// `hideFromExport` is a global style
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<span className={`${styles['header-spinner']} hideFromExport`}>
							<Spinner size="small" delay={0} />
						</span>
					)}
					{id === ADD_FIELDS_COLUMN_ID ? (
						renderAddFieldsColumnHeader()
					) : (
						<div
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={
								styles[collapsible ? 'field-title-wrapper-collapsible' : 'field-title-wrapper']
							}
						>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles['field-title']}>
								{title && (
									<TitleWithIcon
										icon={configurableDate && <Lozenge>D</Lozenge>}
										tooltip={titleTooltip}
										mode="header"
									>
										{title}
									</TitleWithIcon>
								)}
							</div>
							{HeaderContent && <HeaderContent />}
						</div>
					)}
					<SortButton
						isDisabled={currentColumn.attribute === 'priority' && !issuePrioritiesIdList}
						column={currentColumn}
						sorting={sorting}
						changeSort={changeSort}
						customFields={customFields}
						customClass={styles.sortTriggerButton}
						planningUnit={planningUnit}
					/>
				</div>
			</Sections.Item>
		);
	};

	let columnsData;
	let noColumns;

	if (!areCustomFieldsReady || visibleColumns.length === 0) {
		noColumns = renderEmptyFields();
	} else {
		columnsData = visibleColumns.map((column, index, arr) => {
			const data: {
				initialWidth: number | null | undefined;
				minWidth: number;
				isFixed: boolean;
				id: string;
				isLoading: boolean;
				title: string;
				configurableDate?: ConfigurableDate;
				collapsible: boolean | undefined;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				HeaderContent?: ComponentType<any>;
			} = {
				initialWidth: column.defaultWidth,
				minWidth: column.minWidth ?? 24,
				// Last column is flexible to absorb the remaining delta
				isFixed: !(viewMode === VIEW_MODES.LIST && index === arr.length - 1),
				id: column.id,
				isLoading: column.isLoading,
				title: getColumnTitle(column),
				collapsible: column.isCollapsible,
				HeaderContent: column.HeaderContent,
			};

			const configurableDate = getMatchingConfiguredDate(column.id);
			if (configurableDate) {
				data.configurableDate = configurableDate;
				data.initialWidth = DEFAULT_FIELD_WIDTH.LARGE;
			}

			return data;
		});
	}

	const isViewModeList = viewMode === VIEW_MODES.LIST;
	const id = isViewModeList ? SECTION_KEYS.LIST_FIELD : SECTION_KEYS.FIELD;

	const topSectionStyles = isViewModeList
		? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				position: 'sticky',
				left: '0px',
				flex: '0 0 auto',
			} as CSSProperties)
		: undefined;

	const topRightSectionStyles = isViewModeList
		? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				position: 'sticky',
				right: token('space.200', '16px'),
			} as CSSProperties)
		: undefined;

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.header} ref={headerContainer}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<Row className={styles['top-section']}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
				<Left style={topSectionStyles}>
					<AdvancedFieldsMenu
						id="fields-top-section"
						columns={columns}
						hideColumn={hideColumn}
						showColumn={showColumn}
						moveColumn={moveColumn}
						getMatchingConfiguredDate={getMatchingConfiguredDate}
					/>
				</Left>
				{sorting.field !== ISSUE_FIELDS.RANK && !isExportMode && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<Right style={topRightSectionStyles}>
						<ResetSortingOrder onClick={() => changeSort(defaultSorting)} />
					</Right>
				)}
				{!isExportMode && viewMode !== VIEW_MODES.LIST && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<Right className={styles.options}>
						<CollapseButton onClick={onCollapseAllFields} />
					</Right>
				)}
			</Row>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles['column-header-section']}>
				{noColumns || (
					<Sections
						namespace={viewNamespace}
						// @ts-expect-error - TS2322 - Type '{ namespace: string; innerRef: RefObject<unknown>; id: "fields-section"; sectionRenderer: ({ section, onSectionClick, index, setHovered }: SectionRendererArgs) => Element; sectionsData: { ...; }[] | undefined; linkToParent: "main-sections"; onDragStart: () => void; onDragEnd: () => void; width: number; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
						innerRef={sectionsRef}
						id={id}
						sectionRenderer={sectionRenderer}
						sectionsData={columnsData}
						/**
						 * CAVEAT of 'linkToParent' with 'isFitParent'
						 *
						 * There are three "Fields" sections (sticky-header, header, and columns),
						 * only ONE section should be linked to the parent IF the sections have `isFitParent=false`.
						 *
						 * `isFitParent=false` where the section does not bound by parent's size, the parent's width get updated after the section resized,
						 * this will resulted with multiple cascading updates on the parent section IF multiple sections linked to the same parent.
						 *
						 * */
						linkToParent={!isViewModeList ? SECTION_KEYS.MAIN : undefined}
						onDragStart={onDragStart}
						onDragEnd={onDragEnd}
						width={width}
						minWidth={minWidth}
						isFitParent={!isViewModeList}
					/>
				)}
			</div>
		</div>
	);
};

export default injectIntl(withSlots({ AdvancedFieldsMenu: slots.AdvancedFieldsMenu })(Header));
